import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import "../../../../theme/style.scss";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AlertType } from "../../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../../services/common/EncryptionService";
import { PreferencesContext } from "../../../../PreferenceContext";
import { PAGE_SIZE } from "../../../../services/common/const";
import SkeletonWrapper from "../../../../components/Skeleton/SkeletonWrapper";
import { CustomSelect } from "../../../../components/CustomFields/CustomSelect";
import { GetDepartmentList } from "../../../../services/DepartmentService";
import { GetSpecialityList } from "../../../../services/SpecialityService";
import {
  CreateSphereDistant,
  GetSphereDistantDetail,
  UpdateSphereDistant,
} from "../../../../services/Ophthal/OphthalRefractionService";

interface selectOption {
  label: string;
  value: string;
}

interface formInitialValueTypes {
  opthalRefractionMasterId: number;
  code: string;
  name: string;
  departmentId: selectOption | null;
  specialityId: selectOption | null;
  isDeleted: boolean;
}

interface Parameter {
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

const parameters: Parameter = {
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
};

interface MasterSaveProps {
  isForPopUp?: boolean;
  initialName?: string;
  initialDepartmentId?: number;
  initialSpecialityId?: number;
  handleClosePopUp?: (action: string, name?: string) => void;
}

export default function OphthalRefractionDistantSphereSave({
  isForPopUp = false,
  initialName = "",
  initialDepartmentId,
  initialSpecialityId,
  handleClosePopUp,
}: MasterSaveProps = {}) {
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  const urlParams = useQuery();
  const curRawId = urlParams.get("q");
  let curMasterId = 0;
  if (curRawId !== null && curRawId !== undefined) {
    const decId = edService.decrypt(curRawId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      curMasterId = +decId;
    }
  }
  // In popup-create mode this is always a new master, never an Update screen.
  if (isForPopUp) curMasterId = 0;

  const [param] = useState(parameters);
  const [isloading, setIsLoading] = useState<boolean>(true);
  const [departmentData, setdepartmentData] = useState<any[]>([]);
  const [specialityData, setspecialityData] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initFormData: formInitialValueTypes = {
    opthalRefractionMasterId: 0,
    code: "",
    name: "",
    isDeleted: false,
    departmentId:
      Number(initialDepartmentId ?? userContext?.departmentId) > 0
        ? {
          label: userContext?.departmentName || "",
          value: String(initialDepartmentId ?? userContext?.departmentId),
        }
        : null,
    specialityId:
      Number(initialSpecialityId ?? userContext?.specialityId) > 0
        ? {
          label: userContext?.specialityName || "",
          value: String(initialSpecialityId ?? userContext?.specialityId),
        }
        : null,
  };

  const validationSchema = Yup.object().shape({
    opthalRefractionMasterId: Yup.number().nullable(),
    code: Yup.string(),
    name: Yup.string().required("Name is required"),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Department is required"),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Speciality is required"),
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: initFormData,
  });

  // Prefill name when opened as popup with a suggested value
  useEffect(() => {
    if (isForPopUp && initialName) {
      methods.setValue("name", initialName, { shouldValidate: true });
    }
  }, [isForPopUp, initialName, methods]);

  const getMasterDetailById = async () => {
    const params = new URLSearchParams();
    params.append(
      "opthalRefractionSphereDistantId",
      curMasterId.toString()
    );
    params.append("ConsumerId", userContext.consumerId.toString());
    params.append("OrgId", userContext.organizationId.toString());
    params.append("BranchId", userContext.branchId.toString());
    params.append(
      "departmentId",
      userContext?.departmentId?.toString() || "0"
    );
    params.append(
      "specialityId",
      userContext?.specialityId?.toString() || "0"
    );

    try {
      const res: any = await GetSphereDistantDetail(params);
      const master = res?.data?.data;

      methods.reset({
        opthalRefractionMasterId: master?.opthalRefractionMasterId || 0,
        code: master?.code || "",
        name: master?.name || "",
        departmentId: master?.departmentId
          ? {
            value: master.departmentId.toString(),
            label: master.departmentName || "",
          }
          : null,
        specialityId: master?.specialityId
          ? {
            value: master.specialityId.toString(),
            label: master.specialityName || "",
          }
          : null,
        isDeleted: false,
      });

      if (master?.departmentId) {
        await fetchSpeciality(master.departmentId.toString());
      }

      return master;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? e : ((e as any)?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  const saveMaster = async (value: formInitialValueTypes) => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const formData = {
        opthalRefractionMasterId: value.opthalRefractionMasterId,
        ConsumerId: userContext.consumerId.toString(),
        OrgId: userContext.organizationId.toString(),
        BranchId: userContext.branchId.toString(),
        code: value.code?.trim() || "",
        name: value?.name?.trim() || "",
        departmentId: value?.departmentId?.value || null,
        specialityId: value?.specialityId?.value || null,
        DataManagedBy: userContext.loginDetail.userId,
      };
      let result: any;
      if (curMasterId > 0) {
        result = await UpdateSphereDistant(formData);
      } else {
        result = await CreateSphereDistant(formData);
      }
      if (result) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Success,
        });
        if (isForPopUp) {
          handleClosePopUp?.(
            "SAVED",
            value?.name?.trim() || initialName.trim()
          );
        } else {
          navigate(-1);
        }
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string"
            ? (e as string)
            : ((e as any)?.message as string),
        alertType: AlertType.Error,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (data: any) => {
    saveMaster(data);
  };

  const handleBack = () => {
    navigate(-1);
  };

  async function fetchDepartment() {
    try {
      const params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const departmentDataRes: any = await GetDepartmentList(params);
      if (departmentDataRes?.data?.data?.departmentColl) {
        setdepartmentData(departmentDataRes.data.data.departmentColl);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : ((e as any)?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  async function fetchSpeciality(departmentIds: string) {
    try {
      const params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const specialityRes: any = await GetSpecialityList(params);
      if (specialityRes?.data?.data?.specialityColl) {
        setspecialityData(specialityRes.data.data.specialityColl);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : ((e as any)?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  const fetchData = async () => {
    try {
      if (curMasterId <= 0) {
        await fetchDepartment();
        const selectedDepartmentId = Number(
          initialDepartmentId ?? userContext?.departmentId ?? 0
        );
        const selectedSpecialityId = Number(
          initialSpecialityId ?? userContext?.specialityId ?? 0
        );
        if (selectedDepartmentId > 0) {
          await fetchSpeciality(selectedDepartmentId.toString());

          // Re-resolve the department option object from the loaded list
          const departments =
            (
              (await GetDepartmentList(
                (() => {
                  const p = new URLSearchParams();
                  p.append("consumerId", userContext?.consumerId?.toString());
                  p.append("orgId", userContext?.organizationId?.toString());
                  p.append("branchId", userContext?.branchId?.toString());
                  p.append("pageIndex", param.pageIndex.toString());
                  p.append("pageSize", param.pageSize.toString());
                  p.append("sortColumn", param.sortColumn);
                  p.append("sortDirection", param.sortDirection);
                  p.append("searchValue", param.searchValue);
                  p.append("mode", "DROPDOWN");
                  return p;
                })()
              ) as any)?.data?.data?.departmentColl ?? []
            ) || [];
          const departmentOption = departments.find(
            (x: any) =>
              Number(x.value ?? x.departmentId ?? x.id) === selectedDepartmentId
          );
          if (departmentOption) {
            methods.setValue("departmentId", departmentOption, {
              shouldValidate: true,
            });
          }
        }
        if (selectedSpecialityId > 0) {
          const specialities =
            (
              (await GetSpecialityList(
                (() => {
                  const p = new URLSearchParams();
                  p.append("consumerId", userContext?.consumerId?.toString());
                  p.append("orgId", userContext?.organizationId?.toString());
                  p.append("branchId", userContext?.branchId?.toString());
                  p.append("departmentIds", String(selectedDepartmentId));
                  p.append("pageIndex", param.pageIndex.toString());
                  p.append("pageSize", param.pageSize.toString());
                  p.append("sortColumn", param.sortColumn);
                  p.append("sortDirection", param.sortDirection);
                  p.append("searchValue", param.searchValue);
                  p.append("mode", "DROPDOWN");
                  return p;
                })()
              ) as any)?.data?.data?.specialityColl ?? []
            ) || [];
          const specialityOption = specialities.find(
            (x: any) =>
              Number(x.value ?? x.specialityId ?? x.id) === selectedSpecialityId
          );
          if (specialityOption) {
            methods.setValue("specialityId", specialityOption, {
              shouldValidate: true,
            });
          }
        }
      }
      if (curMasterId > 0) {
        await fetchDepartment();
        await getMasterDetailById();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMasterId]);

  return (
    <Box>
      <Box
        className={"main-container"}
        sx={isForPopUp ? { p: 3, m: 0, minWidth: 0 } : undefined}
      >
        {!isForPopUp && (
          <Grid container className="page-header">
            <Grid item xs={6} sm={6} md={6}>
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  alignItems: "center",
                }}
              >
                <CustomBreadcrumb />
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                >
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container spacing={2} gap={0}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.departmentId
                            ? "validation-message"
                            : ""
                          }`}
                      >
                        Department *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isloading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="departmentId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"departmentId"}
                              FieldName={"departmentId"}
                              options={departmentData}
                              isDisabled={curMasterId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e: any) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.departmentId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "departmentId",
                                    e?.value ? e : null,
                                    { shouldValidate: true }
                                  );
                                  methods.setValue("specialityId", null, {
                                    shouldValidate: true,
                                  });

                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchSpeciality(e.value);
                                    setIsLoading(false);
                                  } else {
                                    setspecialityData([]);
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.departmentId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.specialityId
                            ? "validation-message"
                            : ""
                          }`}
                      >
                        Speciality *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isloading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="specialityId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"specialityId"}
                              FieldName={"specialityId"}
                              options={specialityData}
                              isDisabled={curMasterId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e: any) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.specialityId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "specialityId",
                                    e?.value ? e : null,
                                    { shouldValidate: true }
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.specialityId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.code
                            ? "validation-message"
                            : ""
                          }`}
                      >
                        Code
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isloading}
                        className="skeleton-input"
                      >
                        <TextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          {...methods.register("code")}
                          error={!!methods.formState.errors.code}
                          className={`text-input ${methods.formState.errors.code ? "error" : ""
                            }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.code?.message}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.name
                            ? "validation-message"
                            : ""
                          }`}
                      >
                        Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isloading}
                        className="skeleton-input"
                      >
                        <TextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          autoFocus
                          {...methods.register("name", { required: true })}
                          error={!!methods.formState.errors.name}
                          className={`text-input ${methods.formState.errors.name ? "error" : ""
                            }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.name?.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loading={isSubmitting}
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={isSubmitting}
                  >
                    {curMasterId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={() =>
                      isForPopUp
                        ? handleClosePopUp?.("CANCEL")
                        : handleBack()
                    }
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}